import { useCustom, type HttpError } from "@refinedev/core";
import type { GridColumnMetadata, ItemTypeMetadata } from "../types";

interface UseItemTypeMetadataOptions {
  itemTypeName: string;
  enabled?: boolean;
}

interface UseItemTypeMetadataResult {
  metadata: ItemTypeMetadata | undefined;
  gridColumns: GridColumnMetadata[];
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

export const useItemTypeMetadata = ({
  itemTypeName,
  enabled = true,
}: UseItemTypeMetadataOptions): UseItemTypeMetadataResult => {
  // Fetch grid-specific columns (cached by React Query for 30 minutes)
  const {
    data: columnsData,
    isLoading: columnsLoading,
    isError: columnsError,
    error: columnsErr,
    refetch: refetchColumns,
  } = useCustom<GridColumnMetadata[]>({
    url: `/meta/itemtypes/${itemTypeName}/grid-columns`,
    method: "get",
    queryOptions: {
      enabled,
      staleTime: 30 * 60 * 1000, // 30 minutes
    },
  });

  // Fetch full metadata (for forms and other uses)
  const {
    data: metadataData,
    isLoading: metadataLoading,
    isError: metadataError,
    error: metadataErr,
    refetch: refetchMetadata,
  } = useCustom<ItemTypeMetadata>({
    url: `/meta/itemtypes/${itemTypeName}`,
    method: "get",
    queryOptions: {
      enabled,
      staleTime: 30 * 60 * 1000,
    },
  });

  // Extract data from Refine's CustomResponse wrapper
  const metadata = metadataData?.data as ItemTypeMetadata | undefined;
  const gridColumns =
    (columnsData?.data as GridColumnMetadata[] | undefined) ?? [];

  return {
    metadata,
    gridColumns,
    isLoading: columnsLoading || metadataLoading,
    isError: columnsError || metadataError,
    error: columnsErr || metadataErr || null,
    refetch: () => {
      refetchColumns();
      refetchMetadata();
    },
  };
};
